import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

export const brokerageApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "brokerageApi",
    baseUrl: "/api/brokerages",
  }),
  reducerPath: "brokerageApi",
  tagTypes: ["Brokerage"],
  endpoints: (builder) => ({
    getBrokerageById: builder.query<any, string>({
      query: (id) => `/${id}`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Brokerage" }],
    }),
    getBrokerageByCompanyId: builder.query<any, string>({
      query: (companyId) => `/company/${companyId}`,
      keepUnusedDataFor: 0,
      providesTags: [{ type: "Brokerage" }],
    }),
    // REP-225 — brokerages the logged-in user can pick from at deal creation.
    getAvailableBrokerages: builder.query<any, void>({
      query: () => `/available`,
      providesTags: [{ type: "Brokerage" }],
    }),
    // REP-227 — people at a brokerage, for the agent / broker-of-record picker.
    getBrokeragePersons: builder.query<
      any,
      { brokerageId: string; roleType?: string }
    >({
      query: ({ brokerageId, roleType }) =>
        `/${brokerageId}/persons${roleType ? `?roleType=${encodeURIComponent(roleType)}` : ""}`,
      providesTags: [{ type: "Brokerage" }],
    }),
    // REP-225 — set (or clear) the deal-level brokerage anchor on a project.
    setProjectBrokerage: builder.mutation<
      any,
      { projectId: string; brokerageId: string | null }
    >({
      query: ({ projectId, brokerageId }) => ({
        url: `/project/${projectId}`,
        method: "PATCH",
        body: { brokerageId },
      }),
      invalidatesTags: [{ type: "Brokerage" }],
    }),
    createBrokerage: builder.mutation<
      any,
      {
        companyId: string;
        name?: string;
        brokerLicenseNumber?: string;
        officeId?: string;
        mlsId?: string;
      }
    >({
      query: (data) => ({
        url: "/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{ type: "Brokerage" }],
    }),
    updateBrokerage: builder.mutation<
      any,
      {
        id: string;
        data: {
          name?: string;
          brokerLicenseNumber?: string;
          officeId?: string;
          mlsId?: string;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: [{ type: "Brokerage" }],
    }),
    deleteBrokerage: builder.mutation<any, string>({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Brokerage" }],
    }),
  }),
});

export const {
  useGetBrokerageByIdQuery,
  useGetBrokerageByCompanyIdQuery,
  useGetAvailableBrokeragesQuery,
  useGetBrokeragePersonsQuery,
  useSetProjectBrokerageMutation,
  useCreateBrokerageMutation,
  useUpdateBrokerageMutation,
  useDeleteBrokerageMutation,
} = brokerageApi;
